import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getNextLoadStatus } from "@/modules/fit/external/load/_helpers/load-status";

type RelocationOrder = components["schemas"]["RelocationOrder"];

export type RelocationOrderFilterValues = {
  branches: string[];
  brands: string[];
  modelGenerations: string[];
};

export type StatusChange = {
  relocationOrder: RelocationOrder;
  status: RelocationOrderStatus;
};

const DEFAULT_FILTER_VALUES: RelocationOrderFilterValues = {
  branches: [],
  brands: [],
  modelGenerations: [],
};

export type FilterId = keyof RelocationOrderFilterValues;

export function isKnownFilterId(id: string): id is FilterId {
  return id in DEFAULT_FILTER_VALUES;
}

interface FitExternalLoadStoreState {
  loadStatuses: Record<string, RelocationOrderStatus>;
  statusChanges: Record<string, StatusChange>;
  selectedBranch: string | null;
  selectedCity: string | null;
  filterValues: RelocationOrderFilterValues;
  isConfirming: boolean;

  getLoadStatus: (uuid: string) => RelocationOrderStatus;
  cycleLoadStatus: (relocationOrder: RelocationOrder) => void;
  setCannotPickUpReason: (relocationOrder: RelocationOrder, reason: RelocationOrderStatus) => void;
  clearLoadSelections: () => void;
  clearStatusChanges: () => void;
  setSelectedBranch: (branch: string | null) => void;
  setSelectedCity: (city: string | null) => void;
  setFilterValue: (filterId: FilterId, values: string[]) => void;
  setIsConfirming: (isConfirming: boolean) => void;
  reset: () => void;
}

const initialState = {
  loadStatuses: {},
  statusChanges: {},
  selectedBranch: null,
  selectedCity: null,
  filterValues: DEFAULT_FILTER_VALUES,
  isConfirming: false,
} satisfies Omit<
  FitExternalLoadStoreState,
  | "getLoadStatus"
  | "cycleLoadStatus"
  | "setCannotPickUpReason"
  | "clearLoadSelections"
  | "clearStatusChanges"
  | "setSelectedBranch"
  | "setSelectedCity"
  | "setFilterValue"
  | "setIsConfirming"
  | "reset"
>;

export const useFitExternalLoadStore = create<FitExternalLoadStoreState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      getLoadStatus: (uuid) => get().loadStatuses[uuid] ?? RelocationOrderStatus.BranchAwaitingPickup,

      cycleLoadStatus: (relocationOrder) => {
        const currentStatus = get().loadStatuses[relocationOrder.uuid] ?? RelocationOrderStatus.BranchAwaitingPickup;
        const nextStatus = getNextLoadStatus(currentStatus);

        set(
          (state) => ({
            loadStatuses: { ...state.loadStatuses, [relocationOrder.uuid]: nextStatus },
            statusChanges: {
              ...state.statusChanges,
              [relocationOrder.uuid]: { status: nextStatus, relocationOrder },
            },
          }),
          false,
          "cycleLoadStatus",
        );
      },

      setCannotPickUpReason: (relocationOrder, reason) => {
        set(
          (state) => ({
            loadStatuses: { ...state.loadStatuses, [relocationOrder.uuid]: reason },
            statusChanges: {
              ...state.statusChanges,
              [relocationOrder.uuid]: { status: reason, relocationOrder },
            },
          }),
          false,
          "setCannotPickUpReason",
        );
      },

      clearLoadSelections: () =>
        set(
          {
            loadStatuses: {},
            statusChanges: {},
          },
          false,
          "clearLoadSelections",
        ),

      clearStatusChanges: () => set({ statusChanges: {} }, false, "clearStatusChanges"),

      setSelectedBranch: (branch) => set({ selectedBranch: branch, selectedCity: null }, false, "setSelectedBranch"),

      setSelectedCity: (city) => set({ selectedCity: city, selectedBranch: null }, false, "setSelectedCity"),

      setFilterValue: (filterId, values) => {
        const current = get().filterValues[filterId] ?? [];
        const hasChanged = JSON.stringify(current) !== JSON.stringify(values);
        if (!hasChanged) return;

        set(
          (state) => {
            const newValues = { ...state.filterValues, [filterId]: values };
            if (filterId === "brands") {
              newValues.modelGenerations = [];
            }
            return { filterValues: newValues };
          },
          false,
          "setFilterValue",
        );
      },

      setIsConfirming: (isConfirming) => set({ isConfirming }, false, "setIsConfirming"),

      reset: () => set(initialState, false, "reset"),
    }),
    { name: "FitExternalLoadStore" },
  ),
);
